import axios from 'axios';
import { ApiError, DEFAULT_API_ERROR_MESSAGE } from 'types';
import { readToken } from 'services/localStorage.service';
import * as Sentry from '@sentry/react';

type AjvError = {
  instancePath?: string;
  dataPath?: string;
  message?: string;
  params?: {
    missingProperty?: string;
  };
};

type ApiErrorResponse = {
  errors?: unknown;
  message?: unknown;
  msg?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const formatAjvError = (error: unknown): string | undefined => {
  if (!isRecord(error)) return undefined;

  const { dataPath, instancePath, message, params } = error as AjvError;
  if (!message) return undefined;

  const missingProperty = params?.missingProperty;
  const rawPath = [instancePath ?? dataPath, missingProperty]
    .filter(Boolean)
    .join('/');
  const path = rawPath.replace(/^\//, '').replaceAll('/', '.');

  return path ? `${path}: ${message}` : message;
};

const parseApiErrorMessage = (data: unknown): string => {
  if (typeof data === 'string' && data.trim()) return data;

  if (Array.isArray(data)) {
    const messages = data
      .map(formatAjvError)
      .filter((message): message is string => Boolean(message));
    return messages.length > 0
      ? messages.join('; ')
      : DEFAULT_API_ERROR_MESSAGE;
  }

  if (isRecord(data)) {
    const response = data as ApiErrorResponse;
    if (typeof response.message === 'string' && response.message.trim()) {
      return response.message;
    }
    if (typeof response.msg === 'string' && response.msg.trim()) {
      return response.msg;
    }
    if (Array.isArray(response.errors)) {
      return parseApiErrorMessage(response.errors);
    }
  }

  return DEFAULT_API_ERROR_MESSAGE;
};

const normalizeApiEndpoint = (url?: string): string | undefined => {
  const pathSegments = url?.split(/[?#]/, 1)[0]?.split('/').filter(Boolean);
  if (!pathSegments || pathSegments.length === 0) return undefined;

  const controller =
    pathSegments[0] === 'api' ? pathSegments[1] : pathSegments[0];
  return controller && /^[a-z-]+$/.test(controller)
    ? `/${controller}`
    : undefined;
};

const normalizeApiErrorCode = (code?: string): string | undefined =>
  code && /^[A-Z0-9_]+$/.test(code) ? code : undefined;

export const httpApi = axios.create({
  baseURL: '/api/',
});

httpApi.interceptors.request.use((config) => {
  config.headers['Authorization'] = `Bearer ${readToken()}`;

  return config;
});
httpApi.interceptors.response.use(undefined, (error: unknown) => {
  if (axios.isCancel(error)) return Promise.reject(error);
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 500;
    const apiError = new ApiError(
      status,
      status >= 500
        ? DEFAULT_API_ERROR_MESSAGE
        : parseApiErrorMessage(error.response?.data),
      {
        errorCode: normalizeApiErrorCode(error.code),
        failureKind: error.response ? 'http' : 'network',
        method: error.config?.method?.toUpperCase(),
        url: normalizeApiEndpoint(error.config?.url),
      }
    );

    if (status >= 500) {
      Sentry.captureException(apiError);
    }

    if (status === 401) {
      const alink = document.createElement('a');
      alink.href = '/auth/logout';
      alink.click();
      return Promise.reject(apiError);
    } else if (status === 403) {
      const alink = document.createElement('a');
      alink.href = '/Forbidden';
      alink.click();
      return Promise.reject(apiError);
    }

    return Promise.reject(apiError);
  }
  const unknownError =
    error instanceof Error
      ? error
      : new Error('Un endpoint produjo un error desconocido');
  Sentry.captureException(unknownError);
  throw new ApiError(500, DEFAULT_API_ERROR_MESSAGE);
});

export interface ApiErrorData {
  message: string;
  status: number;
}
